import React, { useState } from 'react';
import {
    FileSpreadsheet, Download, Upload, CheckCircle2, AlertTriangle, Loader2, QrCode, Link2,
} from 'lucide-react';
import { baixarArquivoComToken } from '../../../utils/arquivoComToken';

// =====================================================================
// Chamados — CARGA DE EQUIPAMENTO POR PLANILHA + o passo a passo (17/09/2026)
//
// POR QUE ESTA TELA EXISTE
// A fila de Manutenção só abre com o equipamento cadastrado: são dezenas por
// loja × 13 lojas. Cadastrar um a um é o que trava a abertura — e quem vai fazer
// isso não é quem construiu o módulo (o cadastro acontece durante as férias da
// Julia). Daí as duas metades desta tela: a CARGA e o PASSO A PASSO.
//
// ⚠️⚠️ O PASSO A PASSO MORA AQUI, e não num documento: quem está cadastrando está
//    NESTA tela, não com um POP aberto ao lado. Documento que depende de a pessoa
//    lembrar de abrir é documento que não é lido — e o que se perde é justamente
//    a ordem que ninguém adivinha (o equipamento precisa existir ANTES de a
//    etiqueta poder ser vinculada a ele).
//
// ⚠️ ENSAIO ANTES DE GRAVAR, sempre — e o botão de gravar só acende com ZERO
//    problema. Carga parcial deixaria a pessoa sem saber o que entrou, e a
//    segunda tentativa duplicaria o que já estava lá.
// =====================================================================

const COR = 'var(--ch-cor)';
// ⚠️ As variantes translúcidas são variáveis PRÓPRIAS: `var(--ch-cor)22` é CSS
//    inválido — sufixo de alfa não se concatena a uma função. Ver o bloco
//    "MÓDULO CHAMADOS — A COR DA FILA" no index.css.
const COR_A33 = 'var(--ch-cor-aa33)';   // o mesmo matiz, translúcido
const COR_CLARA = 'var(--ch-cor-clara)';
const API = '/api/chamados/ativos';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const caixa = {
    backgroundColor: '#fff', border: `1px solid ${COR_CLARA}`, borderRadius: 12,
    padding: 20, marginBottom: 16,
};

const botao = (primario) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '10px 16px', minHeight: 44, borderRadius: 8, fontWeight: 700, fontSize: 13.5,
    cursor: 'pointer', whiteSpace: 'nowrap',
    border: primario ? 'none' : `1px solid ${COR_A33}`,
    backgroundColor: primario ? COR : '#fff',
    color: primario ? '#fff' : COR,
});

/**
 * @param {string} equipe      a fila em que a pessoa está ('TI' | 'Manutencao')
 * @param {Function} onGravou  chamado depois de uma carga bem-sucedida (recarrega a lista)
 */
const ChamadosAtivosImportar = ({ equipe = 'TI', onGravou, isMobile }) => {
    const [arquivo, setArquivo] = useState(null);
    const [ensaio, setEnsaio] = useState(null);      // o resultado da conferência
    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState('');
    const [aviso, setAviso] = useState('');

    const rotuloFila = equipe === 'Manutencao' ? 'Manutenção' : 'T.I';

    /**
     * Manda a planilha para o servidor.
     *
     * ⚠️⚠️ O MESMO ARQUIVO vai nas duas vezes (ensaio e gravação), e o servidor
     *    reconfere do zero. É o que garante que o que a tela mostrou é o que será
     *    gravado — guardar o resultado do ensaio e mandar "as linhas aprovadas"
     *    abriria a porta para gravar coisa que ninguém viu.
     */
    const enviar = async (gravar) => {
        if (!arquivo) return;
        setOcupado(true); setErro(''); setAviso('');
        try {
            const fd = new FormData();
            fd.append('arquivo', arquivo);
            fd.append('departamento', equipe);
            const r = await fetch(`${API}/importar${gravar ? '?gravar=1' : ''}`, {
                method: 'POST', headers: auth(), body: fd,
            });
            const dados = await r.json().catch(() => ({}));
            if (!r.ok) {
                // ⚠️ A resposta de erro TAMBÉM traz os problemas linha a linha —
                //    mostrá-los é o ponto da tela. Descartá-los e exibir só a
                //    mensagem deixaria a pessoa com "corrija e envie de novo" sem
                //    dizer o que corrigir.
                if (dados.problemas) setEnsaio(dados);
                throw new Error(dados.message || 'Não foi possível conferir a planilha.');
            }
            setEnsaio(dados);
            if (dados.gravado) {
                setAviso(`${dados.criados.length} equipamento(s) cadastrado(s) na fila ${rotuloFila}.`);
                setArquivo(null);
                if (onGravou) onGravou();
            }
        } catch (e) {
            setErro(e.message);
        } finally {
            setOcupado(false);
        }
    };

    const problemas = ensaio?.problemas || [];
    const podeGravar = ensaio && !ensaio.gravado && problemas.length === 0 && ensaio.aGravar > 0;

    return (
        <div style={caixa}>
            {/* ---------------- O PASSO A PASSO ---------------- */}
            <div style={{
                backgroundColor: COR_CLARA, borderRadius: 10, padding: 16, marginBottom: 18,
            }}>
                <p style={{ margin: '0 0 10px 0', fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                    Como colocar um equipamento no ar — os 3 passos, nesta ordem
                </p>
                {/* ⚠️ A ORDEM É A INFORMAÇÃO. A etiqueta só pode ser vinculada a um
                    equipamento que JÁ existe: quem cola o adesivo antes de cadastrar
                    fica com um QR que não resolve nada, e descobre isso na loja. */}
                <ol style={{
                    margin: 0, paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.8,
                }}>
                    <li>
                        <strong>Cadastre o equipamento</strong> — nesta aba, pela planilha (abaixo)
                        ou um a um no formulário. Ele nasce na fila <strong>{rotuloFila}</strong>.
                    </li>
                    <li>
                        <strong>Emita a etiqueta</strong> na aba <strong>Patrimônio</strong> e mande
                        o pacote para a gráfica (ou use uma etiqueta já impressa do lote).
                    </li>
                    <li>
                        <strong>Vincule a etiqueta ao equipamento</strong>, também no Patrimônio, e
                        cole o adesivo. A partir daí o QR abre o chamado já identificado.
                    </li>
                </ol>
                <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#7e22ce', lineHeight: 1.6 }}>
                    <strong>⚠️ A ordem importa:</strong> a etiqueta só se vincula a equipamento que
                    já existe no portal. Colar o adesivo antes de cadastrar deixa um QR que não
                    resolve nada — e isso só se descobre na loja, com o aparelho na mão.
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7e22ce' }}>
                        <QrCode size={14} /> Etiquetas: aba Patrimônio
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7e22ce' }}>
                        <Link2 size={14} /> Vínculo: botão “Vincular” na linha da etiqueta
                    </span>
                </div>
            </div>

            {/* ---------------- A CARGA POR PLANILHA ---------------- */}
            <p style={{
                margin: '0 0 4px 0', fontSize: 14, fontWeight: 800, color: '#0f172a',
                display: 'flex', alignItems: 'center', gap: 8,
            }}>
                <FileSpreadsheet size={17} color={COR} /> Cadastrar vários de uma vez (planilha)
            </p>
            <p style={{ margin: '0 0 14px 0', fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>
                Baixe o modelo, preencha e envie. <strong>Nada é gravado antes de você conferir</strong> —
                o portal mostra o que vai entrar e o que está errado, linha a linha.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* ⚠️⚠️ DOWNLOAD POR `fetch`, NUNCA `<a href>`: a rota é autenticada e o
                    navegador não manda o header `Authorization` ao seguir um link —
                    voltaria 401 numa aba nova, com cara de bug do portal. É a regra
                    do `arquivoComToken.js`, travada por teste. */}
                <button
                    type="button"
                    style={{ ...botao(false), width: isMobile ? '100%' : 'auto' }}
                    disabled={ocupado}
                    onClick={() => baixarArquivoComToken(
                        `${API}/modelo.xlsx?departamento=${encodeURIComponent(equipe)}`,
                        `modelo-equipamentos-${equipe.toLowerCase()}.xlsx`,
                    )}
                >
                    <Download size={16} /> Baixar o modelo
                </button>

                <label style={{ ...botao(false), width: isMobile ? '100%' : 'auto', cursor: 'pointer' }}>
                    <Upload size={16} /> {arquivo ? arquivo.name : 'Escolher planilha'}
                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            setArquivo(e.target.files?.[0] || null);
                            // ⚠️ Zera a conferência anterior: deixar na tela o resumo
                            //    do arquivo antigo, com outro arquivo escolhido, é o
                            //    tipo de coisa que faz a pessoa gravar o que não viu.
                            setEnsaio(null); setErro(''); setAviso('');
                            e.target.value = '';
                        }}
                    />
                </label>

                {arquivo && (
                    <button type="button" style={{ ...botao(true), width: isMobile ? '100%' : 'auto' }}
                        disabled={ocupado} onClick={() => enviar(false)}>
                        {ocupado ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                        Conferir a planilha
                    </button>
                )}
            </div>

            {erro && (
                <p style={{
                    margin: '14px 0 0 0', padding: '10px 12px', borderRadius: 8,
                    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                    color: '#991b1b', fontSize: 13, lineHeight: 1.5,
                }}>
                    {erro}
                </p>
            )}
            {aviso && (
                <p style={{
                    margin: '14px 0 0 0', padding: '10px 12px', borderRadius: 8,
                    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                    color: '#166534', fontSize: 13, fontWeight: 700,
                }}>
                    {aviso}
                </p>
            )}

            {/* ---------------- O RESULTADO DA CONFERÊNCIA ---------------- */}
            {ensaio && !ensaio.gravado && (
                <div style={{
                    marginTop: 16, padding: 14, borderRadius: 10,
                    backgroundColor: problemas.length ? '#fffbeb' : '#f0fdf4',
                    border: `1px solid ${problemas.length ? '#fde68a' : '#bbf7d0'}`,
                }}>
                    <p style={{
                        margin: '0 0 8px 0', fontSize: 13.5, fontWeight: 800,
                        color: problemas.length ? '#92400e' : '#166534',
                    }}>
                        {problemas.length
                            ? `${problemas.length} linha(s) com problema — nada foi gravado`
                            : `${ensaio.aGravar} equipamento(s) prontos para cadastrar`}
                    </p>
                    <p style={{ margin: '0 0 10px 0', fontSize: 12.5, color: '#475569' }}>
                        A planilha tem <strong>{ensaio.total}</strong> linha(s) preenchida(s) ·
                        fila <strong>{rotuloFila}</strong>.
                    </p>

                    {/* ⚠️ A LISTA DE PROBLEMAS TRAZ O NÚMERO DA LINHA DO EXCEL — é por
                        ele que a pessoa acha o erro na planilha dela. Sem o número,
                        "família inválida" vira caça ao tesouro em 300 linhas. */}
                    {problemas.length > 0 && (
                        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {problemas.map((p) => (
                                <div key={p.linha} style={{
                                    display: 'flex', gap: 8, alignItems: 'flex-start',
                                    padding: '7px 0', borderTop: '1px solid #fde68a',
                                }}>
                                    <AlertTriangle size={14} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
                                    <span style={{ fontSize: 12.5, color: '#78350f', lineHeight: 1.5 }}>
                                        <strong>Linha {p.linha}</strong>
                                        {p.valores?.descricao ? ` (${p.valores.descricao})` : ''} — {p.motivos.join(' ')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {podeGravar && (
                        <button type="button" style={{ ...botao(true), marginTop: 12, width: isMobile ? '100%' : 'auto' }}
                            disabled={ocupado} onClick={() => enviar(true)}>
                            {ocupado ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            Cadastrar {ensaio.aGravar} equipamento(s)
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChamadosAtivosImportar;
